// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import type { DateValidationError } from '@mui/x-date-pickers';
import { clientLogger } from '@seedplanner/logger';
import { utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '../../lib/base';
import { EDIT_TASK_GROUP_DATE_MUTATION } from '../../queries/EditTaskGroupDateMutation';
import type {
  EditTaskGroupDateMutation,
  EditTaskGroupDateMutationVariables,
  GetTaskDateDataQuery,
  GetTaskDateDataQueryVariables,
} from '../../types/__generated__/graphql';
import { createID } from '../../utils';
import { ExitDialogButton } from '../common/button';
import InternalDatePicker from '../common/date/InternalDatePicker';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface Props {
  readonly taskId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

const GET_TASK_DATE_DATA_QUERY = gql`
  query GetTaskDateData($id: ID!) {
    task(id: $id) {
      id
      task_time
      planting {
        id
        planting_time
      }
    }
  }
`;

export default function EditTaskDateDialog({ isOpen, onClose, onSuccess, taskId }: Props) {
  const { timezone } = useUserContext();
  const [editTaskGroupDate, { loading, error }] = useMutation<
    EditTaskGroupDateMutation,
    EditTaskGroupDateMutationVariables
  >(EDIT_TASK_GROUP_DATE_MUTATION);
  const [getTaskDateData, { loading: taskDataLoading }] = useLazyQuery<
    GetTaskDateDataQuery,
    GetTaskDateDataQueryVariables
  >(GET_TASK_DATE_DATA_QUERY, { fetchPolicy: 'network-only' });

  React.useEffect(() => {
    if (isOpen) {
      void getTaskDateData({
        variables: {
          id: createID.task(taskId),
        },
        onCompleted: (dataIn) => {
          if (dataIn?.task != null) {
            const taskDataIn = dataIn.task;
            const existingTaskTime = dayjs.unix(taskDataIn.task_time).tz(timezone);
            const maybeExistingPlantingTime =
              taskDataIn.planting?.planting_time != null
                ? dayjs.unix(taskDataIn.planting.planting_time).tz(timezone)
                : null;
            setPlantingTime(maybeExistingPlantingTime);
            setExistingTaskTime(existingTaskTime);
            setTaskTime(existingTaskTime);
          } else {
            enqueueSnackbar('Error getting task data!', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(`Error getting task data: ${errorIn.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getTaskDateData, taskId, timezone]);

  const [taskTime, setTaskTime] = React.useState<dayjs.Dayjs | null>(null);
  const onChangeTaskDate = (newValue: dayjs.Dayjs | null) => {
    setTaskTime(newValue);
  };
  const [taskTimeError, setTaskTimeError] = React.useState<DateValidationError | null>(null);
  const [existingTaskTime, setExistingTaskTime] = React.useState<dayjs.Dayjs | null>(null);
  const [plantingTime, setPlantingTime] = React.useState<dayjs.Dayjs | null>(null);
  const taskTimeErrorMessage = React.useMemo(() => {
    if (taskTimeError == null) {
      return '';
    }
    switch (taskTimeError) {
      case 'minDate':
        // return 'Task date cannot be before today';
        return '';
      case 'maxDate':
        return plantingTime == null ? '' : 'Task date cannot be after harvest date';
      default:
        return '';
    }
  }, [taskTimeError, plantingTime]);

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    if (taskTime == null) {
      enqueueSnackbar('Task date is required', { variant: 'error' });
      return;
    }
    void editTaskGroupDate({
      variables: {
        task_id: taskId,
        new_time: utils.toNoonTime(taskTime, timezone).unix(),
      },
      onCompleted: (data) => {
        if (data !== undefined) {
          enqueueSnackbar('Updated task date', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar(error?.message ?? 'Something went wrong!', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }
      },
      update: (cache, { data }) => {
        const tasksUpdated = (data?.editTaskGroupDate?.updated_tasks?.length ?? 0) > 0;
        cache.modify({
          fields: {
            taskGroup(existingGroup, { DELETE }) {
              return tasksUpdated ? DELETE : existingGroup;
            },
            task_groups(existingGroup, { DELETE }) {
              return tasksUpdated ? DELETE : existingGroup;
            },
          },
        });
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'new_task_error', message: errorIn.message });
      },
    });
  };

  const disableFields = loading || taskDataLoading;
  const isSubmitDisabled = loading || taskDataLoading || taskTime == null || taskTimeErrorMessage !== '';

  const dialogContent = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={0}>
      <InternalDatePicker
        label={'New Task Date *'}
        value={taskTime}
        onChange={onChangeTaskDate}
        disabled={disableFields}
        // minDate={utils.todayStart(timezone)}
        minDate={undefined}
        maxDate={plantingTime ?? undefined}
        onError={(newError) => setTaskTimeError(newError)}
        textFieldProps={{
          helperText: taskTimeErrorMessage,
        }}
      />
      {existingTaskTime && (
        <Typography sx={{ mt: 1 }}>Current task date: {existingTaskTime.format('MM/DD/YYYY')}</Typography>
      )}
      {plantingTime && <Typography sx={{ mt: 1 }}>Harvest date: {plantingTime.format('MM/DD/YYYY')}</Typography>}
      <Typography sx={{ mt: 2 }}>
        Please note that if you create another planting for this crop for the same harvest date, then Microgreen Manager
        will create a new version of this task at the original task date and you will have two versions of this task on
        different dates.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Task Date</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{dialogContent}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button sx={{ width: { xs: '100%', md: '50%' } }} variant="outlined" onClick={onClose} disabled={disableFields}>
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
